import { Component, Input, OnInit } from '@angular/core';
import {
  FilterType,
  TableColumn,
  TableQueryParams
} from '@shared/components/tables/tables.component';
import { ExecutiveDashboardsService } from '@shared/services/executive-dashboards.service';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { CustomEventService } from "@shared/services/custom-event.service";


@UntilDestroy()
@Component({
  selector: 'app-team-activities-report',
  templateUrl: './team-activities-report.component.html',
  styleUrls: ['./team-activities-report.component.scss']
})
export class TeamActivitiesReportComponent implements OnInit {
  private: any;
  teamId: any;
  @Input() selectedDate: any;
  pageSize = 11;
  data: any;
  loading = false;
  initialLoad = true;
  currentParamsString = '';
  listOfLevelName: any[] = [];
  frontPagination = false;
  queryParams: any = {
    widgetEnum: 'TEAM_STATISTICS_REPORT',
    metricType: 'TEAM_STATISTICS_REPORT',
  };
  createExportUrl = `${environment.apiBaseUrl}platform/teams/executive-teams/export`;
  downloadExportUrl = `${environment.apiBaseUrl}platform/export`;
  defaultTableFilter = {levelName: ['isFirstOpen']}

  columns: TableColumn[] = [
    {
      title: 'Teams',
      key: 'teamName',
      resizable: true,
      fixed: 'left',
      width: '200px',
      filterEnabled: true,
      filterType: FilterType.TEXT,
      sortFn: true,
      align: 'left',
      render: (row: any) => `
          <a class="admin-panel__box-header__link"
             href="/organization/${row?.teamId}" target="_blank" title="${row.teamName}">
            ${row.teamName}
            <i class="admin-panel__box-header__icon link-icon"></i>
          </a>
        `
    },
    {
      title: 'Unit Level',
      key: 'levelName',
      resizable: true,
      width: '150px',
      filterEnabled: true,
      filterType: FilterType.DROPDOWN,
      filterOptions: this.listOfLevelName,
      filterFn: true,
      align: 'left'
    },
    {
      title: 'Developers',
      key: 'developerCount',
      resizable: true,
      width: '160px',
      sortable: true,
      comp: 'cellTemplate',
      align: 'right',
      sortFn: (a: any, b: any) => a.developerCount - b.developerCount,
      render: (row: any) => `${row.developerCount}`
    },
    {
      title: 'Average Monthly LoC',
      key: 'lineCountPerDeveloperForMonth',
      resizable: true,
      width: '110px',
      sortable: true,
      sortFn: (a: any, b: any) =>
        a.lineCountPerDeveloperForMonth - b.lineCountPerDeveloperForMonth
    },
    {
      title: 'Average Monthly Commits',
      key: 'commitCountPerDeveloperForMonth',
      resizable: true,
      width: '110px',
      sortable: true,
      sortFn: (a: any, b: any) =>
        a.commitCountPerDeveloperForMonth - b.commitCountPerDeveloperForMonth
    },
    {
      title: 'PRs Created',
      key: 'prCount',
      resizable: true,
      width: '150px',
      sortable: true,
      render: (row: any) => row.pullRequestSummaryReport?.prCount,
      sortFn: (a: any, b: any) =>
        a.pullRequestSummaryReport?.prCount -
        b.pullRequestSummaryReport?.prCount
    },
    {
      title: 'Commits',
      key: 'commitCount',
      resizable: true,
      width: '120px',
      sortable: true,
      render: (row: any) => row.lineOfCodeReport?.commitCount,
      sortFn: (a: any, b: any) =>
        a.lineOfCodeReport?.commitCount - b.lineOfCodeReport?.commitCount
    },
    {
      title: 'Coding Efficiency',
      key: 'efficiencyPercent',
      resizable: true,
      width: '130px',
      sortable: true,
      sortFn: (a: any, b: any) => a.efficiencyPercent - b.efficiencyPercent
    },
    {
      title: 'Coding Impact',
      key: 'totalImpact',
      resizable: true,
      width: '110px',
      sortable: true,
      sortFn: (a: any, b: any) => a.totalImpact - b.totalImpact
    },
    {
      title: 'Insertion (LoC)',
      key: 'addedCommitLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.lineOfCodeReport?.addedCommitLineCount,
      sortFn: (a: any, b: any) =>
        a.lineOfCodeReport?.addedCommitLineCount -
        b.lineOfCodeReport?.addedCommitLineCount
    },
    {
      title: 'Deletion (LoC)',
      key: 'deletedCommitLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.lineOfCodeReport?.deletedCommitLineCount,
      sortFn: (a: any, b: any) =>
        a.lineOfCodeReport?.deletedCommitLineCount -
        b.lineOfCodeReport?.deletedCommitLineCount
    },
    {
      title: 'Edition (LoC)',
      key: 'editedCommitLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.lineOfCodeReport?.editedCommitLineCount,
      sortFn: (a: any, b: any) =>
        a.lineOfCodeReport?.editedCommitLineCount -
        b.lineOfCodeReport?.editedCommitLineCount
    },
    {
      title: 'Total Changes (LoC)',
      key: 'sumOfCommitLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.lineOfCodeReport?.sumOfCommitLineCount,
      sortFn: (a: any, b: any) =>
        a.lineOfCodeReport?.sumOfCommitLineCount -
        b.lineOfCodeReport?.sumOfCommitLineCount
    },
    {
      title: 'Sum Of PR Line',
      key: 'sumOfPRLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.pullRequestSummaryReport?.sumOfPRLineCount,
      sortFn: (a: any, b: any) =>
        a.pullRequestSummaryReport?.sumOfPRLineCount -
        b.pullRequestSummaryReport?.sumOfPRLineCount
    },
    {
      title: 'Added PR LoC',
      key: 'addedPRLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.pullRequestSummaryReport?.addedPRLineCount,
      sortFn: (a: any, b: any) =>
        a.pullRequestSummaryReport?.addedPRLineCount -
        b.pullRequestSummaryReport?.addedPRLineCount
    },
    {
      title: 'Deleted PR LoC',
      key: 'deletedPRLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.pullRequestSummaryReport?.deletedPRLineCount,
      sortFn: (a: any, b: any) =>
        a.pullRequestSummaryReport?.deletedPRLineCount -
        b.pullRequestSummaryReport?.deletedPRLineCount
    },
    {
      title: 'Edited PR LoC',
      key: 'editedPRLineCount',
      resizable: true,
      width: '110px',
      sortable: true,
      render: (row: any) => row.pullRequestSummaryReport?.editedPRLineCount,
      sortFn: (a: any, b: any) =>
        a.pullRequestSummaryReport?.editedPRLineCount -
        b.pullRequestSummaryReport?.editedPRLineCount
    },
    {
      title: 'Monthly Commits',
      key: 'commitCountPerDeveloperForMonth',
      resizable: true,
      width: '110px',
      sortable: true,
      sortFn: (a: any, b: any) =>
        a.commitCountPerDeveloperForMonth - b.commitCountPerDeveloperForMonth
    },
    {
      title: 'Monthly PRs',
      key: 'prCountPerDeveloperForMonth',
      resizable: true,
      width: '110px',
      sortable: true,
      sortFn: (a: any, b: any) =>
        a.prCountPerDeveloperForMonth - b.prCountPerDeveloperForMonth
    }
  ];

  constructor(
    private executiveDashboardsService: ExecutiveDashboardsService,
    private route: ActivatedRoute,
    private customEventService: CustomEventService,
  ) {
    route.params.pipe(untilDestroyed(this)).subscribe(response => {
      this.teamId = response.id;
    });
  }


  // burası initial haldeyken filtreleri sütunları aldığımız kısım
  ngOnInit(): void {
    this.initialLoad = true;
    this.queryParams = {
      ...this.queryParams,
      teamId: this.teamId,
      from: this.selectedDate.from,
      to: this.selectedDate.to
    };

    this.customEventService.eventAnnounced$.pipe(untilDestroyed(this))
      .subscribe(({ action, data }) => {
        if (action === ActionTypesEnum.UPDATE_DURATIONS_DATE) {
          const period = data.period;
          if (data.fromDate) {
            this.selectedDate = {
              period,
              from: data.fromDate,
              to: data.toDate
            };
          } else {
            this.selectedDate = {
              period: data
            };
          }

          this.queryParams = {
            ...this.queryParams,
            from: this.selectedDate.from,
            to: this.selectedDate.to
          };

          this.initialLoad = true;
          this.loadServerDataFn({
            pageIndex: 1,
            pageSize: this.pageSize,
            ...this.queryParams
          }).subscribe((response: any) => {
            this.data = response;
            this.listOfLevelName.length = 0;
            Array.from(new Set(response?.data.map(item => item.levelName)))
              .map(item => ({
                text: item,
                value: item
              }))
              .forEach(item => this.listOfLevelName.push(item));
            this.initialLoad = false;
          });
        }
      });
    // burası skeleton yapısını gösterir
    this.loadServerDataFn({
      pageIndex: 1,
      pageSize: 10,
      ...this.queryParams
    }).subscribe((response: any) => {
      this.data = response;
      this.listOfLevelName.length = 0;
      this.listOfLevelName.push({text: 'Direct Reports Only', value: 'isFirstOpen', selected: true });
      Array.from(new Set(response?.data.map(item => item.levelName)))
        .map(item => ({
          text: item,
          value: item
        }))
        .forEach(item => this.listOfLevelName.push(item));
      this.initialLoad = false;
    });
  }

  // loadServerData fonksiyonunu bir property olarak tanımlıyoruz burası loading i gösterioyr
  loadServerDataFn = (params: any): Observable<any> => {
    // if (this.loading) return of(null); // Eğer zaten yükleme yapılıyorsa yeni istek atma
    if (this.currentParamsString === JSON.stringify(params)) {
      return;
    }

    const { pageIndex, pageSize, sort, filter } = params;
    const currentSort: any = sort?.find((item: any) => item.value !== null);
    const sortField = (currentSort && currentSort.key) || null;
    const sortOrder = (currentSort && currentSort.value) || null;


    // isFirst değeri seçildiyse isFirstOpen göndermeyelim de true gönderelim diye aşağıyı yazdık
    // backend bizden value olarak true beklediği için aşağıda manipüle ettik.
    if (params.filter?.levelName) {
      const isFirtsOpenValue = params.filter?.levelName?.indexOf('isFirstOpen')
      params.filter.levelName = params.filter.levelName.filter(
        (val: any) => val !== 'isFirstOpen'
      );
      if(isFirtsOpenValue > -1) {
        params.filter['isFirstOpen'] = ['true']
      }
    }

    return this.executiveDashboardsService.getTeamsData(
      params.teamId,
      params.widgetEnum,
      params.from,
      params.to,
      filter,
      sortField,
      sortOrder === null ? null : sortOrder === 'ascend' ? 'ASC' : 'DESC',
      pageSize,
      pageIndex,
      null,
    );
  };

  loadClientDataFn = (
    params: TableQueryParams,
    initialLoad: boolean = false
  ): Observable<any> => {
    const allData: any = this.data.data;

    let filteredResults = [...allData];
    if (params.filter && Object.keys(params.filter).length > 0) {
      filteredResults = allData.filter(item => {
        if (!params.filter || typeof params.filter !== 'object') {
          return true;
        }
        return Object.entries(params.filter).every(([key, value]) => {
          return (
            item[key] &&
            item[key]
              .toString()
              .toLowerCase()
              .includes(value.toString().toLowerCase())
          );
        });
      });
    }
    const result = {
      data: filteredResults,
      pagination: {
        total: filteredResults.length,
        size: params.pageSize,
        index: params.pageIndex
      }
    };

    return of(result);
  };
}
